import { doc, getDoc, setDoc, collection, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from './firebase';
import { SavedSignatureTemplate, WorkspaceUser, UserGroup } from '../types';
import { getSavedTemplates, saveTemplate } from './templateGroupService';

const LOCAL_STORAGE_PREFIX = 'cerebrosign_sig_';

/**
 * Strips out any fields with value `undefined` so that Firestore setDoc / updateDoc never throws:
 * "Unsupported field value: undefined"
 */
export function sanitizeForFirestore<T>(data: T): T {
  if (data === null || data === undefined) {
    return null as any;
  }
  if (Array.isArray(data)) {
    return data.map((item) => sanitizeForFirestore(item)).filter((item) => item !== undefined) as any;
  }
  if (typeof data === 'object' && !(data instanceof Date)) {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(data)) {
      if (value !== undefined) {
        cleaned[key] = sanitizeForFirestore(value);
      }
    }
    return cleaned;
  }
  return data;
}

/**
 * Save complete signature configuration to Firestore under:
 * `organizations/{orgId}/signatures/{signatureId}`
 */
export async function saveSignatureToFirestore(
  orgId: string,
  signatureId: string,
  signatureData: Partial<SavedSignatureTemplate>
): Promise<SavedSignatureTemplate> {
  const now = new Date().toISOString();
  const cleanId = signatureId || 'default';
  const org = orgId || 'cerebrolabs.io';

  const payload: SavedSignatureTemplate = {
    id: cleanId,
    name: signatureData.name || 'Domyślny Szablon Firmowy',
    templateId: signatureData.templateId || 'modern_horizontal',
    primaryColor: signatureData.primaryColor || '#0284c7',
    accentColor: signatureData.accentColor,
    toggles: signatureData.toggles || {
      showAvatar: true,
      avatarShape: 'circle',
      showJobTitle: true,
      showDepartment: true,
      showCompany: true,
      showPhone: true,
      showEmail: true,
      showWebsite: true,
      showTaxId: true,
      showAddress: true,
      showSocials: true,
      showBanner: true,
      showDisclaimer: true,
      showFreemiumBranding: false,
    },
    socials: signatureData.socials,
    companyName: signatureData.companyName,
    companyPhone: signatureData.companyPhone,
    companyMobile: signatureData.companyMobile,
    defaultAvatarPlaceholder: signatureData.defaultAvatarPlaceholder,
    websiteUrl: signatureData.websiteUrl,
    nip: signatureData.nip,
    regon: signatureData.regon,
    address: signatureData.address,
    logoUrl: signatureData.logoUrl,
    logoWidth: signatureData.logoWidth,
    logoHeight: signatureData.logoHeight,
    bannerUrl: signatureData.bannerUrl,
    bannerLink: signatureData.bannerLink,
    bannerAlt: signatureData.bannerAlt,
    disclaimer: signatureData.disclaimer,
    promoTitle: signatureData.promoTitle,
    promoText: signatureData.promoText,
    promoCtaText: signatureData.promoCtaText,
    createdAt: signatureData.createdAt || now,
    updatedAt: now,
  };

  // 1. Save in localStorage for immediate offline resilience
  try {
    saveTemplate(org, payload);
    localStorage.setItem(`${LOCAL_STORAGE_PREFIX}${org}_${cleanId}`, JSON.stringify(payload));
  } catch (e) {
    console.warn('LocalStorage save warning:', e);
  }

  // 2. Persist to Firestore: organizations/{orgId}/signatures/{signatureId}
  try {
    const docRef = doc(db, 'organizations', org, 'signatures', cleanId);
    await setDoc(docRef, sanitizeForFirestore(payload), { merge: true });
    console.info(`[Firestore] Signature saved successfully at organizations/${org}/signatures/${cleanId}`);
  } catch (firestoreError: any) {
    console.warn(`[Firestore Warning] Could not persist to Firestore (${firestoreError.message}). Local copy saved.`, firestoreError);
  }

  return payload;
}

/**
 * Load signature configuration from Firestore at:
 * `organizations/{orgId}/signatures/{signatureId}`
 * Falls back to local templates if Firestore is empty or inaccessible.
 */
export async function loadSignatureFromFirestore(
  orgId: string,
  signatureId: string = 'default'
): Promise<SavedSignatureTemplate | null> {
  const org = orgId || 'cerebrolabs.io';
  const cleanId = signatureId || 'default';

  try {
    const docRef = doc(db, 'organizations', org, 'signatures', cleanId);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const data = snap.data() as SavedSignatureTemplate;
      console.info(`[Firestore] Loaded signature from organizations/${org}/signatures/${cleanId}`);
      return data;
    }
  } catch (err: any) {
    console.warn(`[Firestore Warning] Could not fetch organizations/${org}/signatures/${cleanId}:`, err);
  }

  // Fallback: check local storage cache
  try {
    const cached = localStorage.getItem(`${LOCAL_STORAGE_PREFIX}${org}_${cleanId}`);
    if (cached) {
      return JSON.parse(cached);
    }
    const templates = getSavedTemplates(org);
    const found = templates.find((t) => t.id === cleanId) || templates[0];
    return found || null;
  } catch (e) {
    console.warn('LocalStorage load warning:', e);
    return null;
  }
}

/**
 * List all signatures stored in Firestore under `organizations/{orgId}/signatures`
 */
export async function listSignaturesFromFirestore(
  orgId: string
): Promise<SavedSignatureTemplate[]> {
  const org = orgId || 'cerebrolabs.io';
  try {
    const colRef = collection(db, 'organizations', org, 'signatures');
    const snap = await getDocs(colRef);
    if (!snap.empty) {
      const firestoreItems = snap.docs.map((d) => d.data() as SavedSignatureTemplate);
      return firestoreItems;
    }
  } catch (err: any) {
    console.warn(`[Firestore Warning] Could not list signatures from organizations/${org}/signatures:`, err);
  }

  return getSavedTemplates(org);
}

/**
 * Assign signature ID to a specific user in Firestore:
 * Updates `assignedSignatureId` on `organizations/{orgId}/users/{userId}`
 */
export async function assignUserSignatureInFirestore(
  orgId: string,
  userId: string,
  assignedSignatureId: string,
  userEmail?: string
): Promise<void> {
  const org = (orgId && orgId.trim()) || 'cerebrolabs.io';
  const now = new Date().toISOString();
  const targetId = userId || userEmail || '';
  if (!targetId) return;

  const payload: any = {
    id: userId || targetId,
    primaryEmail: userEmail || (targetId.includes('@') ? targetId : ''),
    assignedSignatureId,
    customTemplateId: assignedSignatureId,
    updatedAt: now,
  };

  try {
    const userDocRef = doc(db, 'organizations', org, 'users', targetId);
    await setDoc(userDocRef, sanitizeForFirestore(payload), { merge: true });

    // Also write to userEmail if targetId is a different ID
    if (userEmail && userEmail !== targetId) {
      const emailDocRef = doc(db, 'organizations', org, 'users', userEmail);
      await setDoc(emailDocRef, sanitizeForFirestore(payload), { merge: true });
    }

    console.info(`[Firestore] Assigned signature "${assignedSignatureId}" to user "${targetId}"`);
  } catch (error: any) {
    console.warn(`[Firestore Warning] Could not persist user assignment to Firestore (${error.message})`, error);
  }
}

/**
 * Assign a user to a group (and optionally update their assigned signature) in Firestore:
 * updates organizations/{orgId}/users/{userId} with { groupId, assignedSignatureId?, customTemplateId? }
 */
export async function assignUserGroupInFirestore(
  orgId: string,
  user: { id?: string; primaryEmail?: string },
  groupId: string,
  templateId?: string
): Promise<void> {
  const org = (orgId && orgId.trim()) || 'cerebrolabs.io';
  const targetId = user.id || user.primaryEmail || '';
  if (!targetId) return;

  const now = new Date().toISOString();
  const payload: any = {
    id: user.id || targetId,
    primaryEmail: user.primaryEmail || (targetId.includes('@') ? targetId : ''),
    groupId,
    updatedAt: now,
  };
  if (templateId) {
    payload.assignedSignatureId = templateId;
    payload.customTemplateId = templateId;
  }

  try {
    const userDocRef = doc(db, 'organizations', org, 'users', targetId);
    await setDoc(userDocRef, sanitizeForFirestore(payload), { merge: true });

    // If targetId is different from primaryEmail, sync primaryEmail doc as well
    if (user.primaryEmail && user.primaryEmail !== targetId) {
      const emailDocRef = doc(db, 'organizations', org, 'users', user.primaryEmail);
      await setDoc(emailDocRef, sanitizeForFirestore(payload), { merge: true });
    }

    console.info(`[Firestore] Assigned group "${groupId}" to user "${targetId}" (${user.primaryEmail || ''})`);
  } catch (error: any) {
    console.warn(`[Firestore Warning] Could not persist group assignment for ${targetId}:`, error);
  }
}

/**
 * Batch assign multiple users to a group in Firestore
 */
export async function batchAssignUsersGroupInFirestore(
  orgId: string,
  users: Array<{ id?: string; primaryEmail?: string }>,
  groupId: string,
  templateId?: string
): Promise<void> {
  await Promise.all(
    users.map((u) => assignUserGroupInFirestore(orgId, u, groupId, templateId))
  );
}

/**
 * Batch assign multiple users to a signature / template in Firestore
 */
export async function batchAssignUsersSignatureInFirestore(
  orgId: string,
  userIds: string[],
  assignedSignatureId: string
): Promise<void> {
  await Promise.all(
    userIds.map((userId) => assignUserSignatureInFirestore(orgId, userId, assignedSignatureId))
  );
}

/**
 * Fetch all user overrides (fields, assignedSignatureId, groupId, avatarUrl) stored in Firestore under organizations/{orgId}/users
 */
export async function fetchAssignedUsersFromFirestore(
  orgId: string
): Promise<Record<string, Partial<WorkspaceUser>>> {
  const org = (orgId && orgId.trim()) || 'cerebrolabs.io';
  const result: Record<string, Partial<WorkspaceUser>> = {};

  try {
    const usersCol = collection(db, 'organizations', org, 'users');
    const snap = await getDocs(usersCol);
    snap.forEach((docSnap) => {
      const data = docSnap.data();
      const userItem: Partial<WorkspaceUser> = {
        fullName: data.fullName,
        jobTitle: data.jobTitle,
        department: data.department,
        phone: data.phone,
        mobile: data.mobile,
        showMobile: data.showMobile,
        useCustomPhone: data.useCustomPhone,
        emailAlias: data.emailAlias,
        useCustomWebsite: data.useCustomWebsite,
        customWebsiteUrl: data.customWebsiteUrl,
        useCustomAddress: data.useCustomAddress,
        customAddress: data.customAddress,
        avatarUrl: data.avatarUrl,
        assignedSignatureId: data.assignedSignatureId || data.customTemplateId,
        customTemplateId: data.customTemplateId || data.assignedSignatureId,
        groupId: data.groupId,
      };

      result[docSnap.id] = userItem;
      if (data.primaryEmail) {
        result[data.primaryEmail] = userItem;
      }
      if (data.id) {
        result[data.id] = userItem;
      }
    });
  } catch (err: any) {
    console.warn(`[Firestore Warning] Could not fetch user assignments from organizations/${org}/users:`, err);
  }

  return result;
}

/**
 * Update employee personal data and overrides in Firestore: organizations/{orgId}/users/{userId}
 */
export async function updateUserDataInFirestore(
  orgId: string,
  userId: string,
  userData: Partial<WorkspaceUser>
): Promise<void> {
  const org = (orgId && typeof orgId === 'string' && orgId.trim()) || 'cerebrolabs.io';
  const targetId =
    (userId && typeof userId === 'string' && userId.trim()) ||
    userData.id ||
    userData.primaryEmail ||
    '';

  if (!targetId) {
    console.warn('[Firestore] No valid userId or email provided to updateUserDataInFirestore');
    return;
  }

  try {
    const userDocRef = doc(db, 'organizations', org, 'users', targetId);
    await setDoc(
      userDocRef,
      sanitizeForFirestore({
        ...userData,
        id: targetId,
        updatedAt: new Date().toISOString(),
      }),
      { merge: true }
    );
    console.info(`[Firestore] Updated employee data for "${targetId}" at organizations/${org}/users/${targetId}`);
  } catch (err: any) {
    console.warn(`[Firestore Warning] Could not update user data in Firestore:`, err);
    throw err;
  }
}

/**
 * Update specific user's avatar in Firestore: organizations/{orgId}/users/{userId} -> avatarUrl
 */
export async function updateUserAvatarInFirestore(
  orgId: string,
  userId: string,
  avatarUrl: string
): Promise<void> {
  const org = (orgId && typeof orgId === 'string' && orgId.trim()) || 'cerebrolabs.io';
  const targetId = (userId && typeof userId === 'string' && userId.trim()) || '';
  if (!targetId) {
    console.warn('[Firestore] No valid userId provided to updateUserAvatarInFirestore');
    return;
  }

  try {
    const userDocRef = doc(db, 'organizations', org, 'users', targetId);
    await setDoc(
      userDocRef,
      sanitizeForFirestore({
        avatarUrl,
        updatedAt: new Date().toISOString(),
      }),
      { merge: true }
    );
    console.info(`[Firestore] Updated avatar for user "${targetId}" at organizations/${org}/users/${targetId}`);
  } catch (err: any) {
    console.warn(`[Firestore Warning] Could not update user avatar in Firestore:`, err);
  }
}

/**
 * Save user group configuration (including assigned template) to Firestore under:
 * `organizations/{orgId}/groups/{groupId}`
 */
export async function saveGroupToFirestore(
  orgId: string,
  group: UserGroup
): Promise<void> {
  const org = (orgId && typeof orgId === 'string' && orgId.trim()) || 'cerebrolabs.io';
  if (!group || !group.id) return;

  const now = new Date().toISOString();
  const payload = {
    id: group.id,
    name: group.name,
    description: group.description || '',
    color: group.color || '#0284c7',
    savedTemplateId: group.savedTemplateId || 'tpl_default',
    updatedAt: now,
  };

  try {
    const groupDocRef = doc(db, 'organizations', org, 'groups', group.id);
    await setDoc(groupDocRef, sanitizeForFirestore(payload), { merge: true });
    console.info(`[Firestore] Saved group "${group.name}" (${group.id}) with template "${payload.savedTemplateId}"`);
  } catch (error: any) {
    console.warn(`[Firestore Warning] Could not persist group ${group.id} to Firestore:`, error);
  }
}

/**
 * Fetch all user groups from Firestore under `organizations/{orgId}/groups`
 */
export async function fetchGroupsFromFirestore(
  orgId: string
): Promise<UserGroup[]> {
  const org = (orgId && typeof orgId === 'string' && orgId.trim()) || 'cerebrolabs.io';
  try {
    const groupsCol = collection(db, 'organizations', org, 'groups');
    const snap = await getDocs(groupsCol);
    if (!snap.empty) {
      const groups = snap.docs.map((d) => d.data() as UserGroup);
      console.info(`[Firestore] Fetched ${groups.length} groups from organizations/${org}/groups`);
      return groups;
    }
  } catch (err: any) {
    console.warn(`[Firestore Warning] Could not fetch groups from organizations/${org}/groups:`, err);
  }
  return [];
}

/**
 * Delete a user group from Firestore under `organizations/{orgId}/groups/{groupId}`
 */
export async function deleteGroupFromFirestore(
  orgId: string,
  groupId: string
): Promise<void> {
  const org = (orgId && typeof orgId === 'string' && orgId.trim()) || 'cerebrolabs.io';
  try {
    const groupDocRef = doc(db, 'organizations', org, 'groups', groupId);
    await deleteDoc(groupDocRef);
    console.info(`[Firestore] Deleted group ${groupId} from organizations/${org}/groups`);
  } catch (err: any) {
    console.warn(`[Firestore Warning] Could not delete group ${groupId} from Firestore:`, err);
  }
}

